import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sidebar } from "../includes/sidebar";
import { Topbar } from "../includes/topbar";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";
import { MoreHorizontal, Search, Ban, Eye, CheckCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, CreditCard, Plus } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";

// Mock Data for Cards
const cards = [
    {
        id: "CARD-001",
        number: "**** **** **** 4242",
        holder: "Jean Dupont",
        type: "Physique",
        network: "Visa",
        expiry: "12/26",
        status: "Active",
        balance: "1,200.00 HTG"
    },
    {
        id: "CARD-002",
        number: "**** **** **** 5555",
        holder: "Marie Curie",
        type: "Virtuelle",
        network: "Mastercard",
        expiry: "09/25",
        status: "Bloquée",
        balance: "150.00 USD"
    },
    {
        id: "CARD-003",
        number: "**** **** **** 1234",
        holder: "Albert Einstein",
        type: "Physique",
        network: "Visa",
        expiry: "01/27",
        status: "Active",
        balance: "5,000.00 HTG"
    },
    {
        id: "CARD-004",
        number: "**** **** **** 9876",
        holder: "Entreprise ABC",
        type: "Business",
        network: "Mastercard",
        expiry: "05/28",
        status: "Expirée",
        balance: "50,000.00 HTG"
    },
    {
        id: "CARD-005",
        number: "**** **** **** 8888",
        holder: "James Bond",
        type: "Gold",
        network: "Visa",
        expiry: "11/26",
        status: "Active",
        balance: "Unlimited"
    },
];

export default function CardsPage() {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = React.useState("");
    const [currentPage, setCurrentPage] = React.useState(1);
    const [itemsPerPage, setItemsPerPage] = React.useState(10);
    const [statusFilter, setStatusFilter] = React.useState("Tous");

    const filteredCards = cards.filter(card => {
        const matchesSearch = card.holder.toLowerCase().includes(searchTerm.toLowerCase()) ||
            card.number.includes(searchTerm);
        const matchesStatus = statusFilter === "Tous" || card.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const totalPages = Math.ceil(filteredCards.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentCards = filteredCards.slice(indexOfFirstItem, indexOfLastItem);

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto space-y-4 p-8 pt-6">
                    <div className="flex items-center justify-between space-y-2">
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink className="capitalize">{t('accounts:cards.breadcrumb.accounts')}</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage className="capitalize">{t('accounts:cards.breadcrumb.cards')}</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    </div>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <div className="space-y-1">
                                <CardTitle>{t('accounts:cards.title')}</CardTitle>
                                <CardDescription>
                                    {t('accounts:cards.description')}
                                </CardDescription>
                            </div>
                            <Button size="sm">
                                <Plus className="mr-2 h-4 w-4" /> {t('accounts:cards.newCard')}
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center py-4 space-x-2">
                                <div className="relative w-full max-w-sm">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder={t('accounts:cards.searchPlaceholder')}
                                        className="pl-8"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-[180px]">
                                        <SelectValue placeholder={t('common:labels.status')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Tous">{t('accounts:cards.status.all')}</SelectItem>
                                        <SelectItem value="Active">{t('accounts:cards.status.active')}</SelectItem>
                                        <SelectItem value="Bloquée">{t('accounts:cards.status.blocked')}</SelectItem>
                                        <SelectItem value="Expirée">{t('accounts:cards.status.expired')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="rounded-md border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>{t('accounts:cards.table.card')}</TableHead>
                                            <TableHead>{t('accounts:cards.table.holder')}</TableHead>
                                            <TableHead>{t('accounts:cards.table.type')}</TableHead>
                                            <TableHead>{t('accounts:cards.table.expiry')}</TableHead>
                                            <TableHead>{t('accounts:cards.table.balance')}</TableHead>
                                            <TableHead>{t('accounts:cards.table.status')}</TableHead>
                                            <TableHead className="text-right">{t('common:labels.actions')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {currentCards.map((card) => (
                                            <TableRow key={card.id}>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                                                        <span className="font-mono text-sm">{card.number}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{card.holder}</TableCell>
                                                <TableCell>
                                                    <div className="flex flex-col">
                                                        <span>{card.type}</span>
                                                        <span className="text-xs text-muted-foreground">{card.network}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{card.expiry}</TableCell>
                                                <TableCell>{card.balance}</TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={
                                                        card.status === 'Active' ? 'text-green-600 border-green-600 bg-green-50' :
                                                            card.status === 'Bloquée' ? 'text-red-600 border-red-600 bg-red-50' :
                                                                'text-slate-600 border-slate-600'
                                                    }>
                                                        {card.status === 'Active' ? t('accounts:cards.status.active') :
                                                            card.status === 'Bloquée' ? t('accounts:cards.status.blocked') :
                                                                t('accounts:cards.status.expired')}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" className="h-8 w-8 p-0">
                                                                <span className="sr-only">{t('accounts:agents.actions.openMenu', { defaultValue: 'Open menu' })}</span>
                                                                <MoreHorizontal className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuLabel>{t('common:labels.actions')}</DropdownMenuLabel>
                                                            <DropdownMenuSeparator className="bg-primary-500" />
                                                            <DropdownMenuItem>
                                                                <Eye className="mr-2 h-4 w-4" />
                                                                {t('accounts:cards.actions.details')}
                                                            </DropdownMenuItem>
                                                            {card.status === 'Active' ? (
                                                                <DropdownMenuItem className="text-red-600 focus:text-red-600">
                                                                    <Ban className="mr-2 h-4 w-4" />
                                                                    {t('accounts:cards.actions.block')}
                                                                </DropdownMenuItem>
                                                            ) : card.status === 'Bloquée' ? (
                                                                <DropdownMenuItem className="text-green-600 focus:text-green-600">
                                                                    <CheckCircle className="mr-2 h-4 w-4" />
                                                                    {t('accounts:cards.actions.unblock')}
                                                                </DropdownMenuItem>
                                                            ) : null}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            <div className="flex items-center justify-end space-x-2 py-4">
                                <div className="flex items-center space-x-2">
                                    <p className="text-sm font-medium">{t('common:labels.rowsPerPage')}</p>
                                    <Select
                                        value={`${itemsPerPage}`}
                                        onValueChange={(value) => {
                                            setItemsPerPage(Number(value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <SelectTrigger className="h-8 w-[70px]">
                                            <SelectValue placeholder={itemsPerPage} />
                                        </SelectTrigger>
                                        <SelectContent side="top">
                                            {[5, 10, 20, 50].map((pageSize) => (
                                                <SelectItem key={pageSize} value={`${pageSize}`}>
                                                    {pageSize}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                                    {filteredCards.length > 0 ? (
                                        `${indexOfFirstItem + 1}-${Math.min(indexOfLastItem, filteredCards.length)} ` + t('common:labels.of') + ` ${filteredCards.length}`
                                    ) : (
                                        "0-0 " + t('common:labels.of') + " 0"
                                    )}
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        className="hidden h-8 w-8 p-0 lg:flex"
                                        onClick={() => setCurrentPage(1)}
                                        disabled={currentPage === 1}
                                    >
                                        <span className="sr-only">{t('common:pagination.first', { defaultValue: 'Go to first page' })}</span>
                                        <ChevronsLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="h-8 w-8 p-0"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <span className="sr-only">{t('common:pagination.previous', { defaultValue: 'Go to previous page' })}</span>
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="h-8 w-8 p-0"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <span className="sr-only">{t('common:pagination.next', { defaultValue: 'Go to next page' })}</span>
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="hidden h-8 w-8 p-0 lg:flex"
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                    >
                                        <span className="sr-only">{t('common:pagination.last', { defaultValue: 'Go to last page' })}</span>
                                        <ChevronsRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
